import { Divider, toast } from '@cotera/client/app/components/ui';
import { Card } from '@cotera/client/app/components/headless';
import { Assert } from '@cotera/utilities';
import { useInScopeModel, useWatchableValue } from './actions/framework';
import { GraphModel } from './graph/graph.model';
import { DG } from '@cotera/sdk/core';
import { Tab, useTabParams } from './tab-manager';
import { TabItem } from './components/topbar/tab-item';
import React from 'react';
import { Clipboard } from 'lucide-react';

export const OperationsPage: React.FC = () => {
  const { tabId } = useTabParams<{ tabId: string }>();
  return (
    <>
      <Tab tabId={tabId}>
        <TabItem
          tab={{
            id: tabId,
            title: `Operations`,
            displayTitle: `Operations`,
            icon: 'document-magnifying-glass',
            showClose: true,
          }}
        />
      </Tab>
      <Page />
    </>
  );
};

const Page: React.FC = () => {
  return (
    <div className="flex flex-col gap-4 h-full overflow-y-auto">
      <OperationsView />
    </div>
  );
};

const OperationsView: React.FC = () => {
  const model = useInScopeModel(GraphModel);
  Assert.assert(model !== null, 'Model not found');
  const stagedOperations = useWatchableValue(model.stagedOperations);
  const commits = useWatchableValue(model.publishedCommits).toReversed();

  return (
    <Card.Container className="w-full">
      <Card.Content className="flex flex-col gap-2">
        <Divider caption="Staged Operations" className="mt-4 mb-2" />
        {stagedOperations.map((operation, index) => {
          return <OperationView key={index} op={operation} />;
        })}
        <Divider className="mb-2" caption="Commits" />
        {commits.map((commit, index) => {
          return (
            <div
              key={index}
              className="bg-white rounded-md shadow-sm p-6 w-full hover:shadow-md duration-300"
            >
              <div className="flex flex-row justify-between items-center w-full">
                <div>
                  {commit.message ? (
                    commit.message
                  ) : (
                    <span className="text-gray-400">(No Message)</span>
                  )}
                </div>
                <div>
                  <div>{new Date(commit.timestamp).toDateString()}</div>
                  <div>
                    By: <span>{commit.actorId}</span>
                  </div>
                </div>
              </div>
              <div>
                <CommitOpsExplorer ops={commit.operations} />
              </div>
            </div>
          );
        })}
      </Card.Content>
    </Card.Container>
  );
};

const CommitOpsExplorer: React.FC<{
  ops: DG.OperationData[];
}> = ({ ops }) => {
  return (
    <div className="flex flex-col gap-4 p-4">
      {ops.map((op, i) => (
        <OperationView op={op} key={i} />
      ))}
    </div>
  );
};

const OperationView: React.FC<{
  op: DG.OperationData;
}> = ({ op }) => {
  const opJson = JSON.stringify(op, null, 2);

  return (
    <details>
      <summary>
        <span>{op.t}</span>
      </summary>
      <pre className="bg-gray-800 text-gray-100 p-4 rounded-md overflow-x-auto my-4">
        {opJson}
      </pre>
      <Clipboard
        className="ml-auto h-6 w-6 text-gray-500 cursor-pointer mt-2"
        onClick={async () => {
          await navigator.clipboard.writeText(opJson);
          toast.info('Copied query to clipboard');
        }}
      />
    </details>
  );
};
